import React, { useState } from 'react';
import { View, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { cameraService } from '../../../services/central-api/cameras';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import getBestContrastColor from '../../../services/best-contrast-color';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Filter from './Filters';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import { useQuery } from '@tanstack/react-query';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import WatchVideoModal from './WatchVideoModal';
import { RootNavigation } from '../../../Routes';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';

export default function Downloads({ navigation }: { navigation: RootNavigation; }) {
    const { styles, theme } = useStyles(stylesheet);
    const { user: authenticatedUser } = useAuth();

    const route = useRoute<RouteProp<CamerasParamList, 'Downloads'>>();

    const [filters, setFilters] = useFilters<CamerasParamList['Downloads']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isDownloading, setIsDownloading] = useState(false);

    const [showWatchModal, setShowWatchModal] = useState<boolean>(false);
    const [selectedDownload, setSelectedDownload] = useState<Download | null>(null);
    const [videosUrls, setVideosUrls] = useState<{ [key: number]: { url: string; generatedAt: moment.Moment; }; }>({});

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasDownloads', filters],
        queryFn: () => cameraService.getDownloads(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const { data } = queryResult;

    function handleFilters() {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    async function getVideoUrl(id: number) {
        const validUrl = videosUrls[id];

        if (validUrl) {
            if (moment().diff(validUrl.generatedAt, 'minutes') < 5) {
                return validUrl;
            }
        }

        const generatedAt = moment();
        try {
            setIsDownloading(true);
            const video = await cameraService.getDownload(id);

            const videoUrlObj = {
                url: video.resultUrl,
                generatedAt: generatedAt
            };
            setVideosUrls({
                ...videosUrls,
                [id]: videoUrlObj
            });
            return videoUrlObj;
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsDownloading(false);
        }
    }

    return (
        <Container>
            <Header title={translate('camerasDownloads')} />
            <Card>
                <DataTableHeader
                    title={translate('registeredCamerasDownloads')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true,
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBodyUnit && row.actingBodyUnit.actingBody ? row.actingBodyUnit.actingBody.name : '',
                                wrap: true,
                            },
                            {
                                name: translate('actingBodyUnit'),
                                selector: row => row.actingBodyUnit && row.actingBodyUnit.actingBody ? row.actingBodyUnit.name : '',
                                wrap: true,
                            },
                            {
                                name: translate('user'),
                                selector: row => row.user?.name || '',
                            },
                            {
                                name: translate('camera'),
                                selector: row => row.camera?.title || '',
                                wrap: true,
                            },
                            {
                                name: translate('status'),
                                grow: 0,
                                cell: row =>
                                    <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center' }}>
                                        {row.status == 'pending' ?
                                            <ActivityIndicator size={16} color={theme.colors.loadingColor} /> :
                                            <FontAwesomeIcon
                                                style={styles.statusStyle(row.status)}
                                                fontSize={16}
                                                icon={row.status == 'success' ? faCheckCircle : faTimesCircle}
                                            />
                                        }
                                    </View>
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                            },
                            {
                                name: translate('tags'),
                                cell: row =>
                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5, paddingVertical: 5 }}>
                                        {row.tags ?
                                            row.tags.map((tag) =>
                                                <View key={tag.id} style={[styles.tagMarker, { backgroundColor: tag.color }]}>
                                                    <MyAppText style={styles.markerText(tag.color)}>
                                                        {tag.name}
                                                    </MyAppText>
                                                </View>
                                            ) : null
                                        }</View>
                            },
                            {
                                button: true,
                                cell: row => {
                                    if (row.status !== 'success') {
                                        return null;
                                    }
                                    const actions: Action[] = [];
                                    actions.push({
                                        content: translate('watch'),
                                        onPress: async () => {
                                            const videoUrl = await getVideoUrl(row.id);
                                            if (!videoUrl) {
                                                return;
                                            }
                                            setShowWatchModal(true);
                                            setSelectedDownload(row);
                                        }
                                    });
                                    actions.push({
                                        content: translate('download'),
                                        onPress: async () => {
                                            const videoUrl = await getVideoUrl(row.id);
                                            if (!videoUrl) {
                                                return;
                                            }
                                            openExternalLinkInNewTab(videoUrl.url);
                                        }
                                    });
                                    return <DropdownButton actions={actions} isLoading={isDownloading} size={100} />;
                                }
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={queryResult.isInitialLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            {selectedDownload && <WatchVideoModal
                isModalVisible={showWatchModal}
                setModalVisible={setShowWatchModal}
                onClose={() => {
                    setSelectedDownload(null);
                    setShowWatchModal(false);
                }}
                videoUrl={videosUrls[selectedDownload.id].url}
            />
            }
        </Container>
    );
}

const stylesheet = createStyleSheet((theme) => ({
    statusStyle: (status: VideoAnalysisStatus) => {
        return {
            color: status === 'success' ? theme.colors.successes['500'] : theme.colors.errors['500']
        };
    },
    tagMarker: {
        borderRadius: 8,
        height: 16,
        paddingLeft: 10,
        paddingRight: 10,
        marginRight: 8,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
}));
